import React from 'react'
import { formatPercent } from '../../../shared/lib/formatNumberDe'
import { DASHBOARD_START_MONTH } from '../../../features/dashboard-filters'
import type { DashboardMetric, YearSummary } from '../../../entities/dashboard'
import { formatMetricValue } from './SollIstChart'

interface Props {
  years: YearSummary[]
  metric: DashboardMetric
  /** True when the period contains 2026, which only counts from 01.11. */
  startsMidYear: boolean
}

const cell = 'whitespace-nowrap border-b border-gray-100 py-[7px] pl-2 text-right tabular-nums'

export const SollIstSummary: React.FC<Props> = ({ years, metric, startsMidYear }) => {
  const total = years.reduce((acc, y) => ({ soll: acc.soll + y.soll, ist: acc.ist + y.ist }), { soll: 0, ist: 0 })
  const columns = [
    ...years.map((y) => ({ key: String(y.year), label: y.year === 2026 && startsMidYear ? '2026*' : String(y.year), soll: y.soll, ist: y.ist })),
    ...(years.length > 1 ? [{ key: 'total', label: 'Gesamt', soll: total.soll, ist: total.ist }] : []),
  ]
  const ratio = (soll: number, ist: number) => (soll > 0 ? formatPercent((ist / soll) * 100) : '–')

  return (
    <div className="min-w-0 font-['Roboto'] text-sm text-gray-800">
      <h3 className="mb-2.5 text-xs font-medium uppercase tracking-wide text-slate-500">Zeitraum nach Jahren</h3>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="border-b-2 border-gray-200 py-1.5 text-left font-medium text-gray-700">
              <span className="sr-only">Kennzahl</span>
            </th>
            {columns.map((c) => (
              <th key={c.key} scope="col" className="border-b-2 border-gray-200 py-1.5 pl-2 text-right font-medium text-gray-700">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" className="border-b border-gray-100 py-[7px] text-left font-normal text-gray-500">Soll</th>
            {columns.map((c) => (
              <td key={c.key} className={cell}>{formatMetricValue(metric, c.soll)}</td>
            ))}
          </tr>
          <tr>
            <th scope="row" className="border-b border-gray-100 py-[7px] text-left font-normal text-gray-500">Ist</th>
            {columns.map((c) => (
              <td key={c.key} className={cell}>{formatMetricValue(metric, c.ist)}</td>
            ))}
          </tr>
          <tr className="font-bold text-[#004494]">
            <th scope="row" className="border-b border-gray-100 py-[7px] text-left font-bold">Ist von Soll</th>
            {columns.map((c) => (
              <td key={c.key} className={cell}>{ratio(c.soll, c.ist)}</td>
            ))}
          </tr>
        </tbody>
      </table>
      {startsMidYear && (
        <p className="mt-3.5 text-[13px] text-gray-500">
          * ab 01.{DASHBOARD_START_MONTH.slice(5, 7)}.{DASHBOARD_START_MONTH.slice(0, 4)}
        </p>
      )}
    </div>
  )
}
